import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingState } from '@/components/ui/loading-state';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { classifyAuthError, formatAuthError } from '@/services/auth/errorHandling';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Validate the reset token and get user session
  useEffect(() => {
    const validateToken = async () => {
      try {
        // Check for error in URL params first
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          console.error('Password reset error:', error, errorDescription);
          setError(errorDescription || error);
          setIsValidating(false);
          return;
        }

        // Check for hash fragment that contains auth params
        const hashFragment = window.location.hash;
        if (!hashFragment || !hashFragment.includes('access_token')) {
          console.error('No auth hash fragment found');
          setError('Invalid or expired password reset link. Please request a new password reset.');
          setIsValidating(false);
          return;
        }

        // Get the current session to verify the user is authenticated via reset link
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !session) {
          console.error('No valid session found:', sessionError);
          setError('Invalid or expired password reset link. Please request a new password reset.');
          setIsValidating(false);
          return;
        }

        // Store user email for display
        setUserEmail(session.user.email || '');

        // Mark this session as requiring password reset in localStorage
        // This prevents the user from navigating away without changing password
        localStorage.setItem('password_reset_required', 'true');
        localStorage.setItem('password_reset_user_id', session.user.id);

        // Token is valid and user is authenticated
        setIsValidToken(true);
        setIsValidating(false);
      } catch (error) {
        console.error('Error validating reset token:', error);
        setError('Invalid or expired password reset link. Please request a new password reset.');
        setIsValidating(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const validatePasswords = () => {
    if (!password) {
      setError('Password is required');
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        const classification = classifyAuthError(error);
        const formattedError = formatAuthError(classification);
        setError(`${formattedError.title}. ${formattedError.description}`);
        return;
      }

      // Clear the password reset requirement flags
      localStorage.removeItem('password_reset_required');
      localStorage.removeItem('password_reset_user_id');

      setResetSuccess(true);
      toast({
        title: "Password reset successful",
        description: "Your password has been updated successfully. You will be signed out and can then sign in with your new password."
      });

      // Clear the hash to prevent reprocessing
      window.history.replaceState(null, '', window.location.pathname);

      // Sign out the user to ensure they must log in with new password
      await supabase.auth.signOut();

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/auth/login', { replace: true });
      }, 2000);

    } catch (error: unknown) {
      console.error('Error resetting password:', error);
      const classification = classifyAuthError(error);
      const formattedError = formatAuthError(classification);
      setError(`${formattedError.title}. ${formattedError.description}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <LoadingState
        title="Validating reset link"
        description="Please wait while we verify your password reset link..."
        fullScreen={true}
      />
    );
  }

  // Show success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold tracking-tight text-gray-900">
              Password Reset Successful!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your password has been updated successfully. You have been signed out for security.
              Please log in with your new password.
            </p>
          </div>
          <Button 
            onClick={() => navigate('/auth/login', { replace: true })}
            className="w-full"
          >
            Continue to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-primary">The Mat Monster</h1>
          <h2 className="mt-6 text-3xl font-bold tracking-tight">
            Reset your password
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Enter your new password for {userEmail}
          </p>
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-800">
              <strong>Security Notice:</strong> You must change your password to continue.
              After changing your password, you'll be signed out and need to log in again with your new password.
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="animate-fadeIn">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error}
              {error.includes('Invalid or expired') && (
                <div className="mt-2">
                  <Button
                    variant="secondary"
                    className="bg-white text-destructive hover:bg-white/90 border border-destructive font-medium"
                    onClick={() => navigate('/auth/login', { replace: true })}
                    size="sm"
                  >
                    Back to Login
                  </Button>
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {isValidToken && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="on">
            <div className="space-y-4 rounded-md">
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative mt-1">
                  <Input 
                    id="password" 
                    name="new-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    placeholder="Enter your new password"
                    autoFocus
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Password must be at least 8 characters long
                </p>
              </div>
              
              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative mt-1">
                  <Input 
                    id="confirmPassword" 
                    name="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    placeholder="Confirm your new password"
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Updating password...' : 'Update password'}
              </Button>
            </div>
          </form>
        )}

        <div className="text-center">
          <button 
            type="button" 
            onClick={() => navigate('/auth/login', { replace: true })}
            className="text-sm text-primary hover:text-primary/80 font-medium"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
}
